import { EntityIconType } from '@common/Entity/constants';
import { EntityIcon } from '@common/Entity/EntityIcons';
import { titleCase } from '@common/utils/metadataUtils';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, CircleIcon, LoadingDIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Card, Ellipsis, overlayStyle, ProgressBar, Stack, Text } from '@prophecy/ui-v3';
import { StyledIndicator } from '@prophecy/ui-v3/ProgressBar/styled';
import React, { useState } from 'react';
import styled from 'styled-components';

import { StatusMap, StatusMapKeys } from '../../../config';
export const StyledText = styled(Text)`
  display: inline-grid;
`;

export const StyledMessageCard = styled(Card)<{
  readonly hasError?: boolean;
  readonly isSuccess?: boolean;
  readonly isFocused?: boolean;
  readonly isOverAll?: boolean;
}>`
  border: 1px solid ${theme.colors.gray200};
  padding: ${theme.spaces.x12} ${theme.spaces.x16};
  position: relative;
  width: 100%;
  &&,
  &&:hover {
    box-shadow: none;
  }

  ${({ isFocused }) => (isFocused ? `padding-bottom: ${theme.spaces.x20};` : '')}

  ${({ hasError, isSuccess, isFocused }) =>
    hasError
      ? `
    background: ${isFocused ? theme.colors.white : theme.colors.error25};
    border-color: ${theme.colors.error300};
  `
      : isSuccess
        ? ''
        : `
    background: ${isFocused ? theme.colors.white : theme.colors.gray50};
  `}
  
  ${({ isOverAll }) =>
    isOverAll
      ? ''
      : `
    border-radius: 0 0 ${theme.radius.x8} ${theme.radius.x8};
  `}
`;

const StyledContainer = styled(Stack)`
  position: relative;
`;

const StyledButton = styled(Button)`
  position: absolute;
  right: 0;
  top: 0;
` as unknown as typeof Button;
const StyledProgressBar = styled(ProgressBar)`
  position: absolute;
  right: 0;
  top: 0;
  ${StyledIndicator} {
    background: ${theme.colors.brand200};
  }
`;

const StyledStackIconContainer = styled(Stack)`
  margin-top: ${theme.spaces.x6};
`;

type UploadPercentageType = {
  name?: string;
  status?: StatusMapKeys;
  percent: number;
  hasArrowIcon?: boolean;
  message?: string;
  entity: EntityIconType;
  isError?: boolean;
  isSuccess?: boolean;
  isOverAll?: boolean;
  children?: UploadPercentageType[];
  alwaysShowPercentage?: boolean;
};

const StatusIconUI = ({
  isOverAll,
  isError,
  status,
  isSuccess,
  percent
}: {
  isSuccess?: boolean;
  isOverAll?: boolean;
  isError?: boolean;
  status?: StatusMapKeys;
  percent: number;
}) => {
  if (isOverAll) {
    return null;
  }

  let iconUI = <CircleIcon size='l' type='default' color={theme.colors.gray500} />;
  if (isError && status) {
    iconUI = (
      <Stack direction='horizontal' alignY='center' gap={theme.spaces.x4}>
        {titleCase(status)}
        {StatusMap[status]}
      </Stack>
    );
  } else if (isSuccess) {
    iconUI = <CheckCircleIcon size='m' type='solid' color={theme.colors.greenLight500} />;
  } else if (percent > 0 || status === StatusMapKeys.running) {
    iconUI = <LoadingDIcon size='l' type='extended' color={theme.colors.brand500} spin={true} />;
  }

  return <StyledStackIconContainer alignY='end'>{iconUI}</StyledStackIconContainer>;
};
export const PercentageCard = ({
  isOverAll,
  isError,
  status,
  isSuccess,
  entity,
  message,
  name,
  hasArrowIcon,
  percent,
  alwaysShowPercentage
}: UploadPercentageType) => {
  let textUI = (
    <StyledText level='xs' tone={theme.colors.gray500}>
      <Ellipsis tooltip={true} tooltipProps={{ overlayStyle }}>
        {message || titleCase(status || 'Pending')}
      </Ellipsis>
    </StyledText>
  );

  if (isOverAll) {
    textUI = (
      <>
        <ProgressBar variant={isError ? 'error' : 'loading'} value={percent} width='100%' />
        {(isSuccess || isError) && !alwaysShowPercentage ? null : (
          <Text level='sm' tone={theme.colors.gray500}>
            {parseInt(percent.toString())}%
          </Text>
        )}
      </>
    );
  }

  return (
    <Stack
      direction='horizontal'
      gap={theme.spaces.x16}
      width='100%'
      padding={hasArrowIcon && !isOverAll ? `0 ${theme.spaces.x32} 0 0 ` : ''}>
      <EntityIcon entity={entity} size='m' />
      <Stack gap={theme.spaces.x4} width='100%'>
        <StyledText level='sm' tone={theme.colors.gray700}>
          <Ellipsis tooltip={true} tooltipProps={{ overlayStyle }}>
            {name}
          </Ellipsis>
        </StyledText>
        <Stack direction='horizontal' width='100%' gap={theme.spaces.x8} alignY='center' align='space-between'>
          {textUI}
        </Stack>
      </Stack>
      <StatusIconUI isError={isError} isSuccess={isSuccess} isOverAll={isOverAll} status={status} percent={percent} />
    </Stack>
  );
};
export const UploadPercentageCard = ({
  children = [],
  isError,
  isSuccess,
  ...rest
}: UploadPercentageType & { children?: UploadPercentageType[] }) => {
  const [hasToggle, toggle] = useState(false);
  const isTranspilationDone = isSuccess || isError;
  return (
    <StyledContainer>
      {rest.isOverAll || isTranspilationDone ? null : (
        <StyledProgressBar height='5px' variant={isError ? 'error' : 'loading'} value={rest.percent} width='100%' />
      )}
      <StyledMessageCard
        isOverAll={rest.isOverAll || isTranspilationDone}
        hasError={isError}
        isSuccess={isSuccess}
        isFocused={hasToggle}>
        <StyledContainer gap={theme.spaces.x20} width='100%'>
          <PercentageCard {...rest} isError={isError} isSuccess={isSuccess} />
          {children?.length > 0 ? (
            <StyledButton
              onClick={() => toggle(!hasToggle)}
              variant='plain'
              icon={
                hasToggle ? (
                  <ChevronUpIcon type='default' color={theme.colors.gray500} />
                ) : (
                  <ChevronDownIcon type='default' color={theme.colors.gray500} />
                )
              }
              data-test-id='upload-percentage-card-toggle-button'
              aria-label={hasToggle ? 'Hide details' : 'Show details'}
            />
          ) : null}
          {hasToggle
            ? children.map((child, i) => {
                const childError = child.isError;
                const childSuccess = child.isSuccess;
                const isChildTranspilationDone = childError || childSuccess;
                return (
                  <StyledContainer key={`percentage_${i}`}>
                    {isChildTranspilationDone ? null : (
                      <StyledProgressBar
                        height='5px'
                        variant={childSuccess ? 'error' : 'loading'}
                        value={child.percent}
                        width='100%'
                      />
                    )}
                    <StyledMessageCard
                      isOverAll={child.isOverAll || isChildTranspilationDone}
                      hasError={childError}
                      isSuccess={childSuccess}>
                      <Stack padding={`0 ${theme.spaces.x16} 0 0`}>
                        <PercentageCard {...child} />
                      </Stack>
                    </StyledMessageCard>
                  </StyledContainer>
                );
              })
            : null}
        </StyledContainer>
      </StyledMessageCard>
    </StyledContainer>
  );
};
